// Copyright 2024 The Casdoor Authors. All Rights Reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//      http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React from "react";
import {Spin} from "antd";
import * as Provider from "./Provider";
import * as Setting from "../Setting";
import * as AuthBackend from "./AuthBackend";
import * as Util from "./Util";

// Branded login pages for native/desktop apps, served at /apps/:applicationName/login.
// The OAuth query parameters (client_id, redirect_uri, ...) must be appended to this
// URL by the app, so that provider jumps carry the same state as /login/oauth/authorize.
const APP_BRANDING = {
  "one-team": {
    application: "one-team-desktop",
    displayName: "One Team",
    wordmark: "ONE TEAM",
    logo: "/img/one-team-logo.png",
    eyebrow: "MOBILE COMMAND · 移动控制中心",
    headline: ["你的一人公司，", "尽在口袋。"],
    description: "随时随地连接、监控并指挥你的 AI 编程 Agent —— Mac、PC、服务器和云端的每一个 AI 工作者，汇聚到一个移动工作台。",
  },
};

class AppLoginPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      application: undefined,
      msg: null,
      appKey: props.match?.params?.applicationName ?? "",
    };
  }

  componentDidMount() {
    const branding = this.getBranding();
    document.title = `登录 · ${branding.displayName}`;
    if (branding.logo) {
      let link = document.querySelector("link[rel='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = branding.logo;
    }
    if (this.props.application === undefined) {
      this.getApplication();
    } else {
      this.onUpdateApplication(this.props.application);
    }
  }

  getBranding() {
    return APP_BRANDING[this.state.appKey] ?? {
      application: undefined,
      displayName: this.state.appKey,
      wordmark: this.state.appKey.toUpperCase(),
      logo: undefined,
      eyebrow: "SIGN IN",
      headline: [],
      description: "",
    };
  }

  getApplicationName() {
    const urlParams = new URLSearchParams(this.props.location?.search);
    return urlParams.get("application") ?? this.getBranding().application ?? this.state.appKey;
  }

  getApplicationDisplayName() {
    return this.state.application?.displayName ?? this.state.application?.name ?? this.getApplicationName();
  }

  getApplication() {
    const params = Util.getOAuthGetParameters();
    if (params === null) {
      // The page was opened without OAuth parameters: provider jumps could not
      // return to the calling app, so surface it instead of rendering dead buttons.
      this.onUpdateApplication(null);
      this.setState({msg: "missing-oauth-params"});
      return;
    }

    AuthBackend.getApplicationLogin(params)
      .then((res) => {
        if (res.status === "ok") {
          this.onUpdateApplication(res.data);
        } else {
          this.onUpdateApplication(null);
          this.setState({msg: res.msg});
        }
      })
      .catch(() => {
        this.onUpdateApplication(null);
        this.setState({msg: "network-error"});
      });
  }

  onUpdateApplication(application) {
    if (this.props.onUpdateApplication) {
      this.props.onUpdateApplication(application);
    }
    this.setState({application: application});
  }

  getProviderItem(type) {
    const application = this.state.application;
    if (!application || !application.providers) {
      return null;
    }
    return application.providers.find(providerItem =>
      providerItem.provider && providerItem.provider.type === type && Setting.isProviderVisibleForSignIn(providerItem)
    ) ?? null;
  }

  handleWeChatLogin() {
    const providerItem = this.getProviderItem("WeChat");
    if (!providerItem) {
      return;
    }
    const provider = providerItem.provider;
    const application = this.state.application;

    // Same branch as the standard login page: official-account QR modal vs. direct OAuth jump.
    if (provider.clientId2 !== "" && provider.clientSecret2 !== "" && provider.disableSsl === true && !navigator.userAgent.includes("MicroMessenger")) {
      import("./Util").then(UtilModule => {
        UtilModule.WechatOfficialAccountModal(application, provider, "signup");
      });
    } else {
      window.location.href = Provider.getAuthUrl(application, provider, "signup");
    }
  }

  handleAppleLogin() {
    const providerItem = this.getProviderItem("Apple");
    if (providerItem) {
      window.location.href = Provider.getAuthUrl(this.state.application, providerItem.provider, "signup");
    }
  }

  openExternalLink(url) {
    window.open(url, "_blank", "noopener,noreferrer");
  }

  renderMark(size) {
    // Vector fallback echoing the One Team icon: cream tile, 3x3 blue grid, orange dot top-right.
    const cell = 9.5;
    const gap = 4;
    const origin = 5.75;
    const coords = [origin, origin + cell + gap, origin + 2 * (cell + gap)];
    return (
      <svg className="otl-mark" width={size} height={size} viewBox="0 0 48 48" fill="none" aria-hidden="true">
        <rect width="48" height="48" rx="11" fill="#F2E7D4" />
        {coords.map(y => coords.map(x => (
          <rect key={`${x}-${y}`} x={x} y={y} width={cell} height={cell} rx="3" fill="#5578DC" />
        )))}
        <circle cx={coords[2] + cell / 2} cy={coords[0] + cell / 2} r={cell / 2} fill="#F0A44F" />
      </svg>
    );
  }

  renderBrandLogo(size, className) {
    const branding = this.getBranding();
    if (branding.logo) {
      return <img className={className} src={branding.logo} alt={branding.displayName} width={size} height={size} />;
    }
    return this.renderMark(size);
  }

  renderConstellation() {
    const nodes = [
      {key: "mac", label: "MAC", x: 86, y: 84, status: "run"},
      {key: "pc", label: "PC", x: 52, y: 208, status: "run"},
      {key: "srv", label: "SRV", x: 86, y: 332, status: "sync"},
      {key: "cld", label: "CLD", x: 210, y: 414, status: "run"},
    ];
    const paths = [
      "M100 84 C 200 84, 272 128, 350 172",
      "M66 208 C 180 208, 246 214, 352 218",
      "M100 332 C 200 332, 272 296, 350 262",
      "M224 412 C 292 412, 334 404, 386 392",
    ];

    const glyph = (key) => {
      switch (key) {
      case "mac":
        return <g><rect x="-6" y="-5.5" width="12" height="8" rx="1.5" /><path d="M-8.5 6h17" strokeLinecap="round" /></g>;
      case "pc":
        return <g><rect x="-7" y="-6.5" width="14" height="9.5" rx="1.5" /><path d="M0 3v3M-3.5 6h7" strokeLinecap="round" /></g>;
      case "srv":
        return <g><rect x="-6.5" y="-6.5" width="13" height="13" rx="1.5" /><path d="M-6.5 -2.2h13M-6.5 2.2h13M-3.4 -4.3h.01M-3.4 0.1h.01" strokeLinecap="round" /></g>;
      case "cld":
        return <path d="M-5.2 4h10.6a3.1 3.1 0 0 0 .9-6.07A4.7 4.7 0 0 0-2.9 -4a3.7 3.7 0 0 0-2.3 8z" strokeLinejoin="round" />;
      default:
        return null;
      }
    };

    return (
      <svg className="otl-constellation" viewBox="0 0 560 460" fill="none" aria-hidden="true">
        {/* link paths */}
        {paths.map((d, i) => (
          <React.Fragment key={i}>
            <path d={d} className="otl-link-base" />
            <path d={d} className="otl-link-pulse" style={{animationDelay: `${i * -1.75}s`}} />
          </React.Fragment>
        ))}

        {/* phone */}
        <rect x="352" y="96" width="156" height="300" rx="28" className="otl-phone-body" />
        <rect x="364" y="120" width="132" height="252" rx="14" className="otl-phone-screen" />
        <path d="M414 108h32" strokeLinecap="round" className="otl-phone-notch" />

        {/* mark + agent rows inside the screen */}
        {this.getBranding().logo ?
          <image x="407" y="167" width="46" height="46" href={this.getBranding().logo} /> :
          <g transform="translate(430 190)" color="#DCE6F4">
            <g transform="translate(-21 -21)">{this.renderMark(42)}</g>
          </g>
        }
        <rect x="392" y="252" width="76" height="7" rx="3.5" className="otl-screen-row" style={{animationDelay: "0s"}} />
        <rect x="392" y="270" width="56" height="7" rx="3.5" className="otl-screen-row" style={{animationDelay: ".9s"}} />
        <rect x="392" y="288" width="66" height="7" rx="3.5" className="otl-screen-row" style={{animationDelay: "1.8s"}} />

        {/* device nodes */}
        {nodes.map(node => {
          const labelLeft = node.x > 120 ? node.x - 46 : node.x + 30;
          const labelTop = node.y + 4;
          return (
            <g key={node.key}>
              <rect x={node.x - 16} y={node.y - 16} width="32" height="32" rx="9" className="otl-node-chip" />
              <g transform={`translate(${node.x} ${node.y})`} className={`otl-node-glyph otl-glyph-${node.key}`}>
                {glyph(node.key)}
              </g>
              <text x={labelLeft} y={labelTop} className="otl-node-label">{node.label}</text>
              <circle cx={node.x > 120 ? node.x - 2 : node.x + 22} cy={node.y - 20} r="3"
                className={`otl-node-dot otl-dot-${node.status}`} />
            </g>
          );
        })}
      </svg>
    );
  }

  renderBrandPanel() {
    const branding = this.getBranding();
    return (
      <div className="otl-brand">
        <div className="otl-lockup">
          {this.renderBrandLogo(30, "otl-lockup-img")}
          <span className="otl-wordmark">{branding.wordmark}</span>
        </div>

        <div className="otl-hero">
          <div className="otl-eyebrow">{branding.eyebrow}</div>
          <h1 className="otl-headline">
            {branding.headline.map((line, i) => <span key={i} className="otl-headline-line">{line}</span>)}
          </h1>
          <p className="otl-description">{branding.description}</p>
        </div>

        <div className="otl-constellation-wrap">
          {this.renderConstellation()}
        </div>

        <div className="otl-brand-footer">
          <span>© 2026 MarkAI · One Team</span>
          <span className="otl-footer-mono">MAC · PC · SERVER · CLOUD</span>
        </div>
      </div>
    );
  }

  renderActionsPanel() {
    const branding = this.getBranding();
    const wechatItem = this.getProviderItem("WeChat");
    const appleItem = this.getProviderItem("Apple");

    return (
      <div className="otl-actions">
        <div className="otl-actions-inner">
          <div className="otl-actions-logo">
            {(branding.logo || this.state.application?.logo)
              ? <img src={branding.logo ?? this.state.application.logo} alt={branding.displayName} />
              : <span className="otl-logo-fallback">{this.renderMark(56)}</span>}
          </div>
          <h2 className="otl-title">登录 One Team</h2>
          <p className="otl-subtitle">继续前往 {this.getApplicationDisplayName()}</p>

          {this.renderLoginArea(wechatItem, appleItem)}

          <div className="otl-agreement">
            登录即代表你同意
            <a onClick={() => this.openExternalLink("https://www.markai.com.cn/terms-of-service")}>《用户协议》</a>
            和
            <a onClick={() => this.openExternalLink("https://www.markai.com.cn/privacy-policy")}>《隐私政策》</a>
          </div>
        </div>
      </div>
    );
  }

  renderLoginArea(wechatItem, appleItem) {
    if (this.state.msg === "missing-oauth-params") {
      return (
        <div className="otl-warning">
          <div className="otl-warning-title">缺少 OAuth 参数</div>
          请从 one-team-desktop 发起登录，本页面需要携带 client_id、redirect_uri 等授权参数。
        </div>
      );
    }
    if (this.state.msg !== null) {
      return (
        <div className="otl-warning">
          <div className="otl-warning-title">无法加载登录信息</div>
          {this.state.msg}
        </div>
      );
    }
    if (this.state.application === undefined) {
      return <Spin size="large" style={{marginTop: "36px"}} />;
    }

    return (
      <div className="otl-buttons">
        {wechatItem &&
          <button type="button" className="otl-btn otl-btn-wechat" onClick={() => this.handleWeChatLogin()}>
            <svg width="22" height="22" viewBox="0 0 28 28" aria-hidden="true">
              <path d="M11 4C6.03 4 2 7.13 2 11c0 2.2 1.26 4.16 3.23 5.46L4.5 19.5l3.3-1.65c1 .3 2.08.46 3.2.46.28 0 .55-.01.82-.03A6.06 6.06 0 0 1 11.5 16c0-3.31 3.13-6 7-6 .27 0 .53.01.8.03C18.6 7.02 15.1 4 11 4z" fill="#fff" />
              <circle cx="8" cy="9.4" r="1.15" fill="#07C160" />
              <circle cx="14" cy="9.4" r="1.15" fill="#07C160" />
              <path d="M26 16.2c0-3.04-3.13-5.5-7-5.5s-7 2.46-7 5.5 3.13 5.5 7 5.5c.86 0 1.68-.11 2.44-.32L24.5 23l-.62-2.02C25.16 19.56 26 17.96 26 16.2z" fill="#fff" />
              <circle cx="17" cy="15" r=".95" fill="#07C160" />
              <circle cx="21" cy="15" r=".95" fill="#07C160" />
            </svg>
            <span>微信登录</span>
          </button>
        }
        {appleItem &&
          <button type="button" className="otl-btn otl-btn-apple" onClick={() => this.handleAppleLogin()}>
            <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M17.05 12.54c-.03-2.89 2.36-4.27 2.47-4.34-1.35-1.97-3.44-2.24-4.18-2.27-1.78-.18-3.47 1.05-4.37 1.05-.9 0-2.29-1.02-3.77-.99-1.94.03-3.73 1.13-4.73 2.86-2.02 3.5-.52 8.68 1.45 11.52.96 1.39 2.1 2.95 3.6 2.89 1.45-.06 2-.93 3.75-.93s2.24.93 3.77.9c1.56-.03 2.55-1.41 3.5-2.81 1.1-1.61 1.56-3.17 1.58-3.25-.03-.02-3.04-1.17-3.07-4.63zM14.16 4.06c.8-.97 1.34-2.32 1.19-3.66-1.15.05-2.55.77-3.38 1.74-.74.86-1.39 2.23-1.22 3.55 1.29.1 2.61-.65 3.41-1.63z" fill="#fff" />
            </svg>
            <span>通过 Apple 继续</span>
          </button>
        }
        {!wechatItem && !appleItem &&
          <div className="otl-warning">该应用未配置微信或 Apple 登录方式。</div>
        }
      </div>
    );
  }

  render() {
    return (
      <div className="otl-page">
        {this.renderBrandPanel()}
        {this.renderActionsPanel()}
      </div>
    );
  }
}

export default AppLoginPage;
